import React, { useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Chip,
  styled,
} from '@mui/material';
import {
  SwapHoriz as SwitchIcon,
  Check as CheckIcon,
  Speed as VrpIcon,
  DirectionsCar as FleetIcon,
  Business as EnterpriseIcon,
} from '@mui/icons-material';
import { useDomainTheme, useDomainInfo } from '../../contexts/DomainThemeContext';
import { DomainType } from '../../types/domain';
import { DarkModeToggle } from './DarkModeToggle';

export interface DomainSwitcherProps {
  /** Show as button or menu item */
  variant?: 'button' | 'menuItem';
  /** Show current domain name */
  showLabel?: boolean;
  /** Size variant */
  size?: 'small' | 'medium';
}

const DomainButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  justifyContent: 'flex-start',
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1, 1.5),
  color: theme.palette.text.primary,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const DomainMenuItem = styled(MenuItem)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  minHeight: '48px',
  '&.active': {
    backgroundColor: theme.palette.action.selected,
    '&:hover': {
      backgroundColor: theme.palette.action.selected,
    },
  },
}));

const DomainChip = styled(Chip)(({ theme }) => ({
  height: '20px',
  fontSize: '11px',
  fontWeight: 500,
  '& .MuiChip-label': {
    padding: theme.spacing(0, 1),
  },
}));

/**
 * Get icon for domain type
 */
const getDomainIcon = (domain: DomainType) => {
  switch (domain) {
    case 'vrp':
      return <VrpIcon fontSize="small" />;
    case 'fleet':
      return <FleetIcon fontSize="small" />;
    case 'enterprise':
      return <EnterpriseIcon fontSize="small" />;
    default:
      return <FleetIcon fontSize="small" />;
  }
};

/**
 * Get domain color for chips
 */
const getDomainColor = (domain: DomainType) => {
  switch (domain) {
    case 'vrp':
      return '#2D5A4A';
    case 'fleet':
      return '#3ECF8E';
    case 'enterprise':
      return '#4A90A4';
    default:
      return '#3ECF8E';
  }
};

/**
 * DomainSwitcher Component
 * 
 * Allows users to switch between different DECOY domain themes
 * Features:
 * - Dropdown menu with domain options
 * - Visual indicators for current domain
 * - Domain descriptions and color previews
 * - Keyboard navigation support
 * - Integration with DomainThemeContext
 */
export const DomainSwitcher: React.FC<DomainSwitcherProps> = ({
  variant = 'button',
  showLabel = true,
  size = 'medium',
}) => {
  const { currentDomain, setDomain, availableDomains, getDomainTheme } = useDomainTheme();
  const domainInfo = useDomainInfo();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDomainSelect = (domain: DomainType) => {
    setDomain(domain);
    handleClose();
  };

  if (variant === 'menuItem') {
    return (
      <>
        <MenuItem onClick={handleClick}>
          <ListItemIcon>
            <SwitchIcon />
          </ListItemIcon>
          <ListItemText 
            primary="Switch Domain" 
            secondary={showLabel ? domainInfo.name : undefined}
          />
          {showLabel && (
            <DomainChip
              label={currentDomain.toUpperCase()}
              size="small"
              style={{
                backgroundColor: getDomainColor(currentDomain),
                color: 'white',
              }}
            />
          )}
        </MenuItem>
        
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <Box sx={{ padding: 2, paddingBottom: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Switch Domain
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Choose your DECOY domain theme
            </Typography>
          </Box>
          <Divider />
          
          {availableDomains.map((domain) => {
            const theme = getDomainTheme(domain);
            const isActive = domain === currentDomain;
            
            return (
              <DomainMenuItem
                key={domain}
                onClick={() => handleDomainSelect(domain)}
                className={isActive ? 'active' : ''}
              >
                <ListItemIcon>
                  {getDomainIcon(domain)}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight={isActive ? 600 : 400}>
                        {theme.name}
                      </Typography>
                      {isActive && <CheckIcon fontSize="small" color="primary" />}
                    </Box>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {theme.description}
                    </Typography>
                  }
                />
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: theme.colors.primary,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                />
              </DomainMenuItem>
            );
          })}
        </Menu>
      </>
    );
  }

  return (
    <>
      <DomainButton
        onClick={handleClick}
        startIcon={<SwitchIcon />}
        endIcon={
          showLabel ? (
            <DomainChip
              label={currentDomain.toUpperCase()}
              size="small"
              style={{
                backgroundColor: getDomainColor(currentDomain),
                color: 'white',
              }}
            />
          ) : undefined
        }
        size={size}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        aria-label="Switch domain theme"
      >
        {showLabel && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Typography variant="body2" fontWeight={500}>
              Switch Domain
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {domainInfo.name}
            </Typography>
          </Box>
        )}
        {!showLabel && 'Switch Domain'}
      </DomainButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            minWidth: 280,
            mt: 1,
          },
        }}
      >
        <Box sx={{ padding: 2, paddingBottom: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            DECOY Domain Themes
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Choose your preferred business domain
          </Typography>
        </Box>
        <Divider />
        
        {availableDomains.map((domain) => {
          const theme = getDomainTheme(domain);
          const isActive = domain === currentDomain;
          
          return (
            <DomainMenuItem
              key={domain}
              onClick={() => handleDomainSelect(domain)}
              className={isActive ? 'active' : ''}
            >
              <ListItemIcon>
                {getDomainIcon(domain)}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" fontWeight={isActive ? 600 : 400}>
                      {theme.name}
                    </Typography>
                    {isActive && <CheckIcon fontSize="small" color="primary" />}
                  </Box>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary">
                    {theme.description}
                  </Typography>
                }
              />
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: theme.colors.primary,
                  border: '1px solid',
                  borderColor: 'divider',
                  ml: 1,
                }}
              />
            </DomainMenuItem>
          );
        })}
        
        <Divider />
        
        {/* Dark Mode Toggle */}
        <Box sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Theme Preference
          </Typography>
          <DarkModeToggle variant="switch" showLabel={true} size="small" />
        </Box>
      </Menu>
    </>
  );
};

export default DomainSwitcher;